/**
 * When something happened, in the reader's own time zone.
 *
 * One rule for every "moment" the app shows - an activity entry, a delivered
 * alert, a notification, an acknowledgement:
 *
 *   under a minute  -> "just now"
 *   under an hour   -> "5m ago"
 *   earlier today   -> "3h ago"
 *   yesterday       -> "Yesterday at 3:49 PM"
 *   before that     -> "Sep 21, 3:49 PM" (with the year once it is not this one)
 *
 * No time zone is passed to the Intl formatters, so every stamp renders in the
 * viewer's own system time zone. `dateTimeText` and `dateText` are the absolute
 * forms for tooltips and for fields that are a date rather than a moment.
 */

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function timeText(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** The absolute stamp: date and time, in the viewer's own time zone. The year
    is dropped when it is the current one, and kept otherwise. */
export function dateTimeText(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** A calendar date, no time: for fields that are a day, not a moment. */
export function dateText(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The relative-or-absolute "when", following the rule in this file's header. */
export function whenText(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const now = new Date()
  const diffSec = Math.round((now.getTime() - d.getTime()) / 1000)

  // A future time (or clock skew) has no "ago" reading; show the stamp.
  if (diffSec < 0) return dateTimeText(iso)
  if (diffSec < 45) return 'just now'

  const min = Math.round(diffSec / 60)
  if (min < 60) return `${min}m ago`

  if (isSameDay(d, now)) return `${Math.round(diffSec / 3600)}h ago`

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameDay(d, yesterday)) return `Yesterday at ${timeText(d)}`

  return dateTimeText(iso)
}
